import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CellType } from 'src/app/settings/model/cell-type';
import { ColumnDefinition } from 'src/app/shared/model/column-definition';
import { IconMenuButtonEvent } from 'src/app/shared/model/icon-menu-button-event';
import { TableElementFormatter, TableElementFormatterCollection } from 'src/app/shared/model/table-element-formatter';
import { IconMenuCreator } from 'src/app/shared/util/icon-menu-creator';

// Widok komórki liczony raz na (wiersz, kolumny) w ngOnChanges. Formattery, klasy i style
// nie są wołane z bindingów szablonu, a sam szablon nie potrzebuje per-komórkowych
// NgStyle/NgClass ani łańcucha ngIf - to główny koszt tworzenia/przepinania widoku wiersza
// w wirtualnym scrollu.
interface RowCell {
  kind: 'text' | 'translate' | CellType | 'options';
  column: ColumnDefinition;
  cls: string;
  style: string | null;
  text?: any;
  checked?: boolean;
  hidden?: boolean;
}

const COMPONENT_CELL_TYPES: ReadonlySet<string> = new Set(Object.values(CellType));

@Component({
  selector: 'app-content-row-columns, [app-content-row-columns]',
  templateUrl: './content-row-columns.component.html',
  styleUrls: ['./content-row-columns.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ContentRowColumnsComponent<T> implements OnChanges {

  @Input()
  columns: ColumnDefinition[];
  @Input()
  row: T;
  @Input()
  expandableDeviceStatusRow: boolean;
  @Input()
  expandableSnvrTask: boolean;
  @Input()
  hasMenuOptions: boolean;
  @Input()
  cellFormatters: TableElementFormatterCollection<any>;
  @Input()
  iconMenuCreator: IconMenuCreator<T>;
  @Input()
  optionsCellCreator: IconMenuCreator<T>;
  @Output()
  contextMenuSelected = new EventEmitter<ContextMenuSelected<T>>();
  @Output()
  rowButtonClicked = new EventEmitter<IconMenuButtonEvent<T>>();
  @Output()
  invokeRefresh = new EventEmitter();
  @Output()
  violationClicked = new EventEmitter<ViolationClickEvent<T>>();
  @Input()
  expanded: boolean;
  @Input()
  rowIndex: number;
  @Input()
  showRowNumbers: boolean;

  cells: RowCell[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    // Pozostałe inputy (rowIndex, expanded, showRowNumbers, kreatory) dotyczą komórki opcji
    // i komórek specjalnych, które bindują się w szablonie bezpośrednio.
    if (changes.row || changes.columns || changes.cellFormatters) {
      this.buildCells();
    }
  }

  private buildCells(): void {
    this.cells = (this.columns ?? []).map(column => this.buildCell(column));
  }

  private buildCell(column: ColumnDefinition): RowCell {
    if (column.name === 'options') {
      return { kind: 'options', column, cls: '', style: null };
    }
    const formatterCls = this.getCellFormatter(column.name)?.createElementCssClasses(this.row);
    const cls = (column.wrap ? 'wrap' : 'ellipsis') + (formatterCls ? ' ' + formatterCls : '');
    const style = buildInlineStyle(column.style);
    // Kolejność jak w dawnym szablonie: translatable ma pierwszeństwo przed cellType.
    if (column.translatable) {
      return { kind: 'translate', column, cls, style, text: this.formatValue(column) };
    }
    const cellType = column.formatterParams?.cellType;
    if (cellType === CellType.CHECKBOX) {
      return {
        kind: cellType, column, cls: cls + ' center', style,
        checked: !!this.formatValue(column), hidden: column.cellHidden ? column.cellHidden(this.row) : false
      };
    }
    if (cellType && COMPONENT_CELL_TYPES.has(cellType)) {
      return { kind: cellType, column, cls, style };
    }
    return { kind: 'text', column, cls, style, text: this.formatValue(column) };
  }

  private formatValue(column: ColumnDefinition): any {
    return column.formatExt ? column.formatExt(this.row) : column.format(this.row[column.name]);
  }

  trackCell = (_index: number, cell: RowCell): string => cell.column.name;

  get rowNumber(): string {
    const n = this.rowIndex + 1;
    return n + '.';
  }

  get rowNumberFontSize(): string {
    const n = this.rowIndex + 1;
    if (n > 9999) return '7px';
    if (n > 999) return '8px';
    return '9px';
  }

  getCellFormatter(fieldName: string): TableElementFormatter<any> | null {
    return this.cellFormatters ? this.cellFormatters[fieldName] : null;
  }

  // Stan checkboxa czyta się z wiersza (column.formatExt), więc komórki trzeba przeliczyć ręcznie -
  // OnPush nie dostanie tu nowego inputu.
  toggleCheckbox(cell: RowCell): void {
    this.rowButtonClicked.emit({ buttonAction: cell.column.name, data: this.row });
    this.buildCells();
  }

  // Klik w komórkę z checkboxem nie zaznacza wiersza; gdy checkboxa nie ma (cellHidden), zaznacza
  // normalnie.
  onCheckboxCellClicked(event: MouseEvent, cell: RowCell): void {
    if (!cell.hidden)
      event.stopPropagation();
  }

  handleViolationClick(violation: number): void {
    this.violationClicked.emit(Object.assign({}, {
      violation,
      row: this.row
    }));
  }

}

// Styl kolumny (format ngStyle: 'prop' lub 'prop.unit', dopuszczalny camelCase) sklejany raz
// do stringa inline - bez dyrektywy NgStyle i jej differa na każdej komórce.
function buildInlineStyle(style?: Record<string, string>): string | null {
  if (!style) return null;
  return Object.entries(style)
    .map(([key, value]) => {
      const [prop, unit] = key.split('.');
      const cssProp = prop.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
      return `${cssProp}: ${value}${unit ?? ''}`;
    })
    .join('; ');
}

export interface ContextMenuSelected<T> {
  event: MouseEvent;
  data: T;
}


export type ViolationClickEvent<T> = { violation: number; row: T };
